import { useState, useEffect } from 'react';
import { Trophy, Medal, Crown, Star, TrendingUp, Zap, Timer } from 'lucide-react';
import { azureClient } from '../lib/azure-public';
import type { QuizConfig, API } from '@solace-quiz/shared';

type LeaderboardEntry = API.Leaderboard.Entry & {
  rank: number;
  name: string;
};

interface LeaderboardProps {
  quizId: string;
}

export default function Leaderboard({ quizId }: LeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();

    // Poll for updates every 10 seconds (replaces Supabase realtime)
    const interval = setInterval(() => {
      loadLeaderboard(false);
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [quizId]);

  const loadLeaderboard = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }

    try {
      setLoadError(null);
      const leaderboardData = await azureClient.leaderboard.get(quizId);

      // Transform API response to match component format
      const formattedEntries: LeaderboardEntry[] = leaderboardData.map((entry, index) => ({
        rank: index + 1,
        name: entry.session.name || 'Anonymous',
        session: entry.session,
        answers: entry.answers,
        stats: entry.stats,
      }));

      setEntries(formattedEntries);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setLoadError(`Quiz "${quizId}" not found. Please check the URL and try again.`);
    }

    setIsLoading(false);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-8 h-8 text-yellow-500" />;
      case 2:
        return <Medal className="w-7 h-7 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-orange-600" />;
      default:
        return null;
    }
  };

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-br from-brand-green to-green-600 text-brand-dark shadow-lg shadow-brand-green/50 scale-110';
      case 2:
        return 'bg-gradient-to-br from-brand-light to-brand-green/50 text-brand-dark shadow-lg shadow-brand-green/30 scale-105';
      case 3:
        return 'bg-gradient-to-br from-brand-green/70 to-brand-green/40 text-brand-dark shadow-lg shadow-brand-green/20';
      default:
        return 'bg-gray-200 text-brand-dark';
    }
  };

  const getDesignationColor = (designation: string) => {
    if (designation.includes('Genius')) {
      return 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-yellow-900 border-0 shadow-lg shadow-yellow-500/50';
    } else if (designation.includes('Contextual Strategist')) {
      return 'bg-gradient-to-r from-brand-green/80 to-brand-light text-brand-dark border-0';
    } else if (designation.includes('Adaptive Reasoner')) {
      return 'bg-gradient-to-r from-brand-green/60 to-brand-light/80 text-brand-dark border-0';
    } else if (designation.includes('Developing')) {
      return 'bg-gradient-to-r from-brand-light to-brand-light/60 text-brand-dark border-0';
    } else {
      return 'bg-gray-100 text-brand-dark border-gray-300 border-2';
    }
  };

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-brand-dark mb-4">Quiz Not Found</h1>
          <p className="text-gray-600 mb-6">{loadError}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center">
        <div className="text-center">
          <Zap className="w-16 h-16 text-brand-green animate-bounce mx-auto mb-4" />
          <p className="text-xl font-bold text-brand-dark">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-brand-green animate-pulse" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading text-brand-dark">
              Leaderboard
            </h1>
            <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-brand-green animate-pulse" />
          </div>
          <p className="text-base sm:text-xl text-brand-dark/70 font-medium">
            Top performers ranked by score. Oh, and if you're looking for a top-performing agentic AI platform, talk to us about Solace Agent Mesh.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-brand-dark/60">
            <TrendingUp className="w-4 h-4" />
            <span>{entries.length} players competing</span>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-brand-dark/60">No entries yet. Be the first to compete!</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {entries.slice(0, 3).map((entry, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ring-2 sm:ring-4 ring-opacity-50 ${
                    entry.rank === 1
                      ? 'ring-brand-green transform hover:scale-[1.02]'
                      : entry.rank === 2
                      ? 'ring-brand-light transform hover:scale-[1.01]'
                      : 'ring-brand-green/50 transform hover:scale-[1.01]'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-6">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl sm:text-2xl transition-transform ${getRankBadgeColor(entry.rank)}`}>
                        {getRankIcon(entry.rank)}
                      </div>

                      <div className="flex-1 min-w-0 sm:hidden">
                        <div className="text-2xl sm:text-3xl font-bold font-heading text-brand-dark">
                          {entry.stats.aiIqDisplay}
                        </div>
                        <div className="text-xs text-brand-dark/60 font-medium">
                          AI IQ Score
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                      <div className="mb-2">
                        <h3 className="text-xl sm:text-2xl font-bold font-heading text-brand-dark truncate">
                          {entry.name}
                        </h3>
                        <span className={`inline-block mt-1 px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${getDesignationColor(entry.stats.designation)}`}>
                          {entry.stats.designation}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-brand-dark/60 mt-2">
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-brand-green" />
                          <span className="whitespace-nowrap">{entry.stats.correctAnswers}/{entry.stats.totalAnswers}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Timer className="w-3 h-3 sm:w-4 sm:h-4 text-brand-green" />
                          <span className="font-mono whitespace-nowrap">{formatTime(entry.stats.totalTimeMs)}</span>
                        </span>
                      </div>
                    </div>

                    <div className="hidden sm:block flex-shrink-0 text-right">
                      <div className="text-3xl sm:text-4xl font-bold font-heading text-brand-dark">
                        {entry.stats.aiIqDisplay}
                      </div>
                      <div className="text-sm text-brand-dark/60 font-medium">
                        AI IQ Score
                      </div>
                      <div className="mt-2">
                        <div className="w-24 sm:w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-brand-green to-brand-light rounded-full transition-all duration-500"
                            style={{ width: `${(entry.stats.aiIq / 150) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {entry.rank === 1 && (
                    <div className="bg-gradient-to-r from-brand-light/50 to-brand-light px-6 py-3 border-t-2 border-brand-green">
                      <div className="flex items-center justify-center gap-2 text-brand-dark font-bold">
                        <Zap className="w-5 h-5" />
                        <span>Current Leader</span>
                        <Zap className="w-5 h-5" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {entries.length > 3 && (
              <>
                <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-brand-light/30 to-brand-light/50 border-b-2 border-brand-green/30">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold text-brand-dark">Rank</th>
                          <th className="px-6 py-4 text-left text-sm font-bold text-brand-dark">Player</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-brand-dark">Designation</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-brand-dark">Correct</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-brand-dark">Time</th>
                          <th className="px-6 py-4 text-right text-sm font-bold text-brand-dark">Score</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {entries.slice(3).map((entry, index) => (
                          <tr key={index} className="hover:bg-brand-light/20 transition-colors">
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center justify-center w-10 h-10 bg-brand-light/50 text-brand-dark font-bold rounded-full">
                                #{entry.rank}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-semibold font-heading text-brand-dark">{entry.name}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getDesignationColor(entry.stats.designation)}`}>
                                {entry.stats.designation}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-1 text-sm text-brand-dark/70">
                                <Star className="w-4 h-4 text-brand-green" />
                                <span>{entry.stats.correctAnswers}/{entry.stats.totalAnswers}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-1 text-sm text-brand-dark/70">
                                <Timer className="w-4 h-4 text-brand-green" />
                                <span className="font-mono">{formatTime(entry.stats.totalTimeMs)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="font-bold text-lg font-heading text-brand-dark">{entry.stats.aiIqDisplay}</div>
                              <div className="text-xs text-brand-dark/60">AI IQ</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="md:hidden space-y-3">
                  {entries.slice(3).map((entry, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-brand-light/50 text-brand-dark font-bold rounded-full text-sm">
                            #{entry.rank}
                          </span>
                          <div className="font-bold font-heading text-brand-dark">{entry.name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold font-heading text-brand-dark">{entry.stats.aiIqDisplay}</div>
                          <div className="text-xs text-brand-dark/60">AI IQ</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-brand-dark/60">
                        <span className={`px-2 py-1 rounded-full font-bold ${getDesignationColor(entry.stats.designation)}`}>
                          {entry.stats.designation}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-brand-green" />
                            {entry.stats.correctAnswers}/{entry.stats.totalAnswers}
                          </span>
                          <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3 text-brand-green" />
                            <span className="font-mono">{formatTime(entry.stats.totalTimeMs)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        <div className="mt-8 sm:mt-12 text-center">
          <a
            href={`/${quizId}`}
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-brand-green text-brand-dark font-bold text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl hover:opacity-90 transform hover:scale-105 transition-all duration-300"
          >
            <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
            Start Your Quiz
          </a>
        </div>
      </div>
    </div>
  );
}
